import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { toast } from 'react-toastify';
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DateObject from "react-date-object";
import { 
  createBankPaymentRequest, 
  getUserBankPaymentRequests,
  BankPaymentRequest 
} from '../services/bankPaymentService';
import { getRequestById, TestRequest } from '../services/requestService';
import { formatPersianDateTime, formatPersianDate } from '../utils/persianDate';
import ModernFileUploader from '../components/ui/ModernFileUploader';
import { validateIranianNationalCode } from '../utils/validation';
import API_CONFIG from '../config/api';

// Transaction interface
interface Transaction {
  _id: string;
  amount: number;
  description: string;
  status: 'pending' | 'completed' | 'failed';
  gateway: string;
  refId?: string;
  paymentDate?: string;
  createdAt: string;
  transactionType: 'request' | 'remaining';
}

// Translate status to Persian
const translateStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده',
    no_payment_needed: 'عدم نیاز به پرداخت',
    paid: 'پرداخت شده'
  };
  return statusMap[status] || status;
};

// Get status color
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    no_payment_needed: 'bg-blue-100 text-blue-800',
    paid: 'bg-emerald-100 text-emerald-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

const BankPayment: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<BankPaymentRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  
  // For remaining payment flow
  const [testRequest, setTestRequest] = useState<TestRequest | null>(null);
  const [loadingTestRequest, setLoadingTestRequest] = useState(false);
  const [creatingPayment, setCreatingPayment] = useState(false);
  
  // Transaction history
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Record<string, Transaction[]>>({});
  const [loadingTransactions, setLoadingTransactions] = useState<Record<string, boolean>>({});
  
  // Form state
  const [formData, setFormData] = useState({
    admissionNumber: '',
    nationalId: '',
    firstName: '',
    lastName: '',
    testDate: '',
    description: '',
    receiptImage: null as File | null,
    receiptImageUrl: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Persian date state for test date
  const [selectedTestDate, setSelectedTestDate] = useState<DateObject | null>(null);

  // Check for openTab parameter on component mount
  useEffect(() => {
    const openTab = searchParams.get('openTab');
    if (openTab === '1') {
      setActiveTab('history');
    }
  }, [searchParams]);

  // Check for requestId parameter for remaining payment flow
  useEffect(() => {
    const requestId = searchParams.get('requestId');
    if (requestId) {
      fetchTestRequest(requestId);
    }
  }, [searchParams]);

  const fetchTestRequest = async (requestId: string) => {
    try {
      setLoadingTestRequest(true);
      const response = await getRequestById(requestId);
      if (response.success && response.data) {
        setTestRequest(response.data);
        // Switch to history tab to show the payment option
        setActiveTab('history');
      } else {
        toast.error('درخواست یافت نشد یا دسترسی به آن ندارید');
        // Don't redirect, just show error and stay on the page
      }
    } catch (error: any) {

      if (error.response?.status === 404) {
        toast.error('درخواست یافت نشد');
      } else if (error.response?.status === 401) {
        toast.error('لطفا ابتدا وارد شوید');
        navigate('/login');
      } else {
        toast.error('خطا در دریافت اطلاعات درخواست');
      }
      // Don't redirect to my-tests, stay on the bank payment page
    } finally {
      setLoadingTestRequest(false);
    }
  };

  // Load user's requests on component mount
  useEffect(() => {
    if (activeTab === 'history') {
      fetchUserRequests();
    }
  }, [activeTab]);

  const fetchUserRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await getUserBankPaymentRequests({ limit: 20 });
      if (response.success) {
        setRequests(response.data as BankPaymentRequest[]);
      }
    } catch (error) {

      toast.error('خطا در دریافت لیست درخواست‌ها');
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleReceiptUpload = (file: File, fileUrl: string) => {
    setFormData(prev => ({
      ...prev,
      receiptImage: file,
      receiptImageUrl: fileUrl
    }));
    if (errors.receiptImage) {
      setErrors(prev => ({ ...prev, receiptImage: '' }));
    }
  };

  const handleTestDateChange = (date: DateObject | null) => {
    setSelectedTestDate(date);
    if (date) {
      // Convert Persian date to Gregorian date string for backend (same as ReceiveImageReport)
      const gregorianDate = date.toDate(); // This converts to JavaScript Date object
      const isoDate = gregorianDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      setFormData(prev => ({
        ...prev,
        testDate: isoDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        testDate: ''
      }));
    }
    if (errors.testDate) {
      setErrors(prev => ({ ...prev, testDate: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.admissionNumber.trim()) {
      newErrors.admissionNumber = 'شماره پذیرش الزامی است';
    }

    if (!formData.nationalId.trim()) {
      newErrors.nationalId = 'کد ملی الزامی است';
    } else if (!validateIranianNationalCode(formData.nationalId)) {
      newErrors.nationalId = 'کد ملی معتبر نیست';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'نام الزامی است';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'نام خانوادگی الزامی است';
    }

    if (!formData.testDate) {
      newErrors.testDate = 'تاریخ انجام آزمایش الزامی است';
    }

    if (!formData.receiptImage) {
      newErrors.receiptImage = 'آپلود تصویر قبض الزامی است';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const requestData = {
        patientInfo: {
          admissionNumber: formData.admissionNumber,
          nationalId: formData.nationalId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          testDate: formData.testDate
        },
        description: formData.description,
        receiptImage: formData.receiptImage!
      };

      const response = await createBankPaymentRequest(requestData);
      
      if (response.success) {
        toast.success('درخواست شما با موفقیت ثبت شد');
        
        // Reset form
        setFormData({
          admissionNumber: '',
          nationalId: '',
          firstName: '',
          lastName: '',
          testDate: '',
          description: '',
          receiptImage: null,
          receiptImageUrl: ''
        });
        setSelectedTestDate(null);
        setErrors({});
        
        // Switch to history tab and refresh
        setActiveTab('history');
        fetchUserRequests();
      } else {
        toast.error(response.message || 'خطا در ثبت درخواست');
      }
    } catch (error) {

      toast.error('خطا در ثبت درخواست');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (paymentUrl: string, request: any) => {
    // If it's a relative path, it should be an API endpoint
    if (paymentUrl.startsWith('/')) {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          toast.error('لطفا ابتدا وارد شوید');
          return;
        }

        // Prepare payment data for remaining payment
        const paymentData = {
          amount: request.adminResponse.paymentAmount,
          basePrice: request.adminResponse.paymentAmount,
          description: `پرداخت باقی مانده - درخواست ${request.patientInfo.admissionNumber}`,
          callbackUrl: `${window.location.origin}/payment-callback`,
          returnUrl: `${window.location.origin}/bank-payment`,
          email: '',
          mobile: request.userId?.mobile || '',
          userId: request.userId?._id || request.userId,
          planName: 'پرداخت باقی مانده',
          planPeriod: 1,
          discount: 0,
          purchaseMonth: new Date().getMonth() + 1,
          samplingLocation: 'lab', // Default for bank payments
          freeTransportation: true, // Bank payments are typically lab-based
          samplingDate: request.patientInfo.testDate,
          samplingTime: '09:00', // Default time
          transactionType: 'remaining', // Important: mark as remaining payment
          bankPaymentRequestId: request._id // Add bank payment request ID
        };

        const response = await fetch(`${API_CONFIG.baseURL}${paymentUrl.replace(/^\/api/, '')}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(paymentData)
        });

        const data = await response.json();
        if (data.success && data.url) {
          window.location.href = data.url; // Redirect to payment gateway
        } else {
          toast.error(data.message || 'خطا در ایجاد پرداخت');
        }
      } catch (error) {

        toast.error('خطا در ایجاد پرداخت');
      }
    } else {
      // If it's already a full URL, open it directly
      window.open(paymentUrl, '_blank');
    }
  };

  const createRemainingPayment = async () => {
    if (!testRequest) return;

    try {
      setCreatingPayment(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('لطفا ابتدا وارد شوید');
        return;
      }

      // Calculate total amount including transportation
      const baseAmount = testRequest.amount || 0;
      const transportationPrice = testRequest.samplingLocation?.freeTransportation ? 0 : 50000; // 50,000 Toman for transportation
      const totalAmount = baseAmount + transportationPrice;

      const paymentRequest = {
        amount: baseAmount,
        basePrice: baseAmount,
        description: `پرداخت باقی مانده - درخواست آزمایش ${testRequest.displayId || testRequest._id.slice(-8)}`,
        callbackUrl: `${window.location.origin}/payment-callback`,
        returnUrl: `${window.location.origin}/test-requests/${testRequest._id}`,
        email: '',
        mobile: testRequest.patientInfo?.mobile || '',
        userId: testRequest.userId,
        planName: 'پرداخت باقی مانده',
        planPeriod: 1,
        discount: 0,
        purchaseMonth: new Date().getMonth() + 1,
        samplingLocation: testRequest.samplingLocation?.type || 'lab',
        freeTransportation: testRequest.samplingLocation?.freeTransportation || false,
        samplingDate: testRequest.samplingDate,
        samplingTime: testRequest.samplingTime,
        transactionType: 'remaining', // Add transaction type
        testRequestId: testRequest._id // Add test request ID for proper transaction linking
      };

      const response = await fetch(`${API_CONFIG.baseURL}/payments/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(paymentRequest)
      });

      const data = await response.json();
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.message || 'خطا در ایجاد پرداخت');
      }
    } catch (error) {

      toast.error('خطا در ایجاد پرداخت');
    } finally {
      setCreatingPayment(false);
    }
  };

  // Fetch transactions for a bank payment request
  const fetchTransactionsForRequest = async (bankPaymentRequestId: string) => {
    try {
      setLoadingTransactions(prev => ({ ...prev, [bankPaymentRequestId]: true }));
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }

      const response = await fetch(`${API_CONFIG.baseURL}/payments/bank-payment-request/${bankPaymentRequestId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTransactions(prev => ({ ...prev, [bankPaymentRequestId]: data.data || [] }));
        }
      }
    } catch (error) {

    } finally {
      setLoadingTransactions(prev => ({ ...prev, [bankPaymentRequestId]: false }));
    }
  };

  // Toggle expanded request and fetch transactions
  const toggleExpandRequest = (requestId: string) => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId(null);
    } else {
      setExpandedRequestId(requestId);
      // Fetch transactions if not already fetched
      if (!transactions[requestId]) {
        fetchTransactionsForRequest(requestId);
      }
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">پرداخت باقیمانده</h1>
          <p className="text-gray-600">درخواست پرداخت باقیمانده قبض‌های آزمایشگاهی</p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" dir="rtl">
              <button
                onClick={() => setActiveTab('new')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'new'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                ایجاد درخواست جدید
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'history'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                پیگیری درخواست‌ها
              </button>
            </nav>
          </div>
        </div>

        {/* New Request Form */}
        {activeTab === 'new' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">اطلاعات بیمار</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Admission Number */}
                <div>
                  <label htmlFor="admissionNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    شماره پذیرش <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="admissionNumber"
                    name="admissionNumber"
                    value={formData.admissionNumber}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.admissionNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="شماره پذیرش را وارد کنید"
                  />
                  {errors.admissionNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.admissionNumber}</p>
                  )}
                </div>

                {/* National ID */}
                <div>
                  <label htmlFor="nationalId" className="block text-sm font-medium text-gray-700 mb-2">
                    کد ملی <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="nationalId"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.nationalId ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="کد ملی را وارد کنید"
                    maxLength={10}
                  />
                  {errors.nationalId && (
                    <p className="mt-1 text-sm text-red-600">{errors.nationalId}</p>
                  )}
                </div>

                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    نام <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="نام را وارد کنید"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    نام خانوادگی <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="نام خانوادگی را وارد کنید"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>

                {/* Test Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    تاریخ انجام آزمایش <span className="text-red-500">*</span>
                  </label>
                  <DatePicker
                    value={selectedTestDate}
                    onChange={handleTestDateChange}
                    calendar={persian}
                    locale={persian_fa}
                    calendarPosition="bottom-right"
                    inputClass="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="تاریخ انجام آزمایش را انتخاب کنید"
                  />
                  {errors.testDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.testDate}</p>
                  )}
                </div>
              </div>

              {/* Receipt Image Upload */}
              <div>
                <ModernFileUploader
                  id="receiptImage"
                  label="آپلود تصویر قبض"
                  onChange={handleReceiptUpload}
                  required={true}
                  accept="image/*,application/pdf"
                  error={errors.receiptImage}
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  توضیحات (اختیاری)
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="توضیحات اضافی را وارد کنید"
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading && (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  ثبت درخواست
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Request History */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">تاریخچه درخواست‌ها</h2>
            
            {/* Remaining Payment Section */}
            {testRequest && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">پرداخت باقی مانده</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">شناسه درخواست:</span>
                    <span className="font-medium">{testRequest.displayId || testRequest._id.slice(-8)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">نام بیمار:</span>
                    <span className="font-medium">{testRequest.patientInfo?.firstName} {testRequest.patientInfo?.lastName}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">مبلغ پایه:</span>
                    <span className="font-medium">{testRequest.amount ? `${new Intl.NumberFormat('fa-IR').format(testRequest.amount)} تومان` : 'رایگان'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">ایاب و ذهاب:</span>
                    <span className="font-medium">{testRequest.samplingLocation?.freeTransportation ? 'رایگان' : '50,000 تومان'}</span>
                  </div>
                  <div className="flex justify-between items-center border-t pt-3">
                    <span className="text-sm font-semibold text-gray-800">مجموع قابل پرداخت:</span>
                    <span className="text-lg font-bold text-green-600">
                      {testRequest.amount ? 
                        `${new Intl.NumberFormat('fa-IR').format((testRequest.amount || 0) + (testRequest.samplingLocation?.freeTransportation ? 0 : 50000))} تومان` : 
                        'رایگان'
                      }
                    </span>
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={createRemainingPayment}
                      disabled={creatingPayment || loadingTestRequest}
                      className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {creatingPayment && (
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      {creatingPayment ? 'در حال ایجاد پرداخت...' : '💳 پرداخت آنلاین'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {loadingRequests ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">هیچ درخواستی یافت نشد</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ردیف
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        شماره پذیرش
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        وضعیت درخواست
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        تاریخ و ساعت درخواست
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        تاریخ و ساعت پاسخ
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        جزئیات
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {requests.map((request, index) => (
                      <React.Fragment key={request._id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {request.patientInfo.admissionNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(request.status)}`}>
                            {translateStatus(request.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatPersianDateTime(request.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {request.adminResponse?.responseDate 
                            ? formatPersianDateTime(request.adminResponse.responseDate)
                            : '---'
                          }
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="space-y-2">
                            {/* Patient Info */}
                            <div className="text-xs text-gray-600">
                              <div className="font-medium">
                                {request.patientInfo.firstName} {request.patientInfo.lastName}
                              </div>
                              <div>کد ملی: {request.patientInfo.nationalId}</div>
                            </div>
                            
                            {/* Payment Amount or Rejection Reason */}
                            {request.status === 'approved' && request.adminResponse?.paymentAmount && (
                              <div className="bg-green-50 p-2 rounded border border-green-200">
                                <div className="text-green-700 font-medium text-sm">
                                  💰 مبلغ قابل پرداخت: {request.adminResponse.paymentAmount.toLocaleString('fa-IR')} تومان
                                </div>
                                {request.paymentInfo?.paymentUrl && (
                                  <button
                                    onClick={() => handlePayment(request.paymentInfo!.paymentUrl!, request)}
                                    className="mt-2 inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors"
                                  >
                                    <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                    </svg>
                                    پرداخت
                                  </button>
                                )}
                              </div>
                            )}
                            
                            {request.status === 'paid' && request.adminResponse?.paymentAmount && (
                              <div className="bg-emerald-50 p-2 rounded border border-emerald-200">
                                <div className="text-emerald-700 font-medium text-sm">
                                  ✅ پرداخت با موفقیت انجام شد
                                </div>
                                <div className="text-emerald-600 text-xs mt-1">
                                  💰 مبلغ پرداخت شده: {request.adminResponse.paymentAmount.toLocaleString('fa-IR')} تومان
                                </div>
                                {request.paymentInfo?.paidAt && (
                                  <div className="text-emerald-600 text-xs">
                                    📅 تاریخ و ساعت پرداخت: {formatPersianDateTime(request.paymentInfo.paidAt)}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {request.status === 'rejected' && request.adminResponse?.rejectionReason && (
                              <div className="bg-red-50 p-2 rounded border border-red-200">
                                <div className="text-red-700 text-xs">
                                  <strong>دلیل عدم تایید:</strong>
                                  <div className="mt-1">{request.adminResponse.rejectionReason}</div>
                                </div>
                              </div>
                            )}
                            
                            {request.status === 'no_payment_needed' && (
                              <div className="bg-blue-50 p-2 rounded border border-blue-200">
                                <div className="text-blue-700 text-xs font-medium">
                                  ✅ عدم نیاز به پرداخت باقیمانده
                                </div>
                              </div>
                            )}
                            
                            {/* Description */}
                            {request.description && (
                              <div className="text-xs text-gray-500">
                                <strong>توضیحات:</strong> {request.description}
                              </div>
                            )}
                            
                            {/* دکمه مشاهده تراکنش‌ها */}
                            {(request.status === 'approved' || request.status === 'paid') && (
                              <button
                                onClick={() => toggleExpandRequest(request._id)}
                                className="mt-2 inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-md hover:bg-purple-200 transition-colors"
                              >
                                {expandedRequestId === request._id ? '▼' : '◀'} تراکنش‌ها
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded row for transactions */}
                      {expandedRequestId === request._id && (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 bg-gray-50">
                            <div className="p-4 bg-white rounded border border-gray-200">
                              <h4 className="text-sm font-bold text-gray-700 mb-3">تاریخچه تراکنش‌ها</h4>
                              {loadingTransactions[request._id] ? (
                                <div className="text-center py-4">
                                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
                                  <p className="mt-2 text-sm text-gray-600">در حال بارگذاری...</p>
                                </div>
                              ) : transactions[request._id] && transactions[request._id].length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">ردیف</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">مبلغ</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">وضعیت</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">درگاه</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">کد رهگیری</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">تاریخ</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {transactions[request._id].map((transaction, index) => (
                                        <tr key={transaction._id}>
                                          <td className="px-3 py-2 text-xs text-gray-900">{index + 1}</td>
                                          <td className="px-3 py-2 text-xs font-medium text-gray-900">
                                            {new Intl.NumberFormat('fa-IR').format(transaction.amount)} تومان
                                          </td>
                                          <td className="px-3 py-2">
                                            <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${
                                              transaction.status === 'completed' ? 'bg-green-100 text-green-800' : 
                                              transaction.status === 'failed' ? 'bg-red-100 text-red-800' : 
                                              'bg-yellow-100 text-yellow-800'
                                            }`}>
                                              {transaction.status === 'completed' ? 'موفق' : 
                                               transaction.status === 'failed' ? 'ناموفق' : 
                                               'در انتظار'}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-xs text-gray-900">
                                            {transaction.gateway === 'parsian' ? 'پارسیان' : transaction.gateway}
                                          </td>
                                          <td className="px-3 py-2 text-xs text-gray-900 font-mono">
                                            {transaction.refId || '---'}
                                          </td>
                                          <td className="px-3 py-2 text-xs text-gray-900">
                                            {new Intl.DateTimeFormat('fa-IR', { 
                                              year: 'numeric', 
                                              month: 'numeric', 
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            }).format(new Date(transaction.paymentDate || transaction.createdAt))}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 text-center py-4">هیچ تراکنشی یافت نشد</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {requests.length > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={fetchUserRequests}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  بارگذاری مجدد
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BankPayment; 